import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useOliverName } from '../contexts/OliverContext';
import './Sidebar.css';
import logoOliverSmall from './img/Logo Oliver small.png';

/** Style des tooltips du menu desktop : fond noir, radius, texte clair (aligné librairie tooltip) */
const menuTooltipSlotProps = {
  popper: {
    sx: {
      '& .MuiTooltip-tooltip': {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderRadius: '8px',
        fontSize: 13,
        padding: '8px 12px',
      },
      '& .MuiTooltip-arrow': {
        color: '#1a1a1a',
      },
    },
  },
};

const Sidebar = ({ isOpen, onToggle, children, position = 'left', logoUrl = null, profile = null, onAvatarClick = null, isConnected = false, onLogoClick = null, activePage = null, compactMenuItems = null }) => {
  const { oliverName } = useOliverName();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [isDarkTheme, setIsDarkTheme] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('theme-dark')
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevIsOpenRef = useRef(isOpen);

  // Smartphone/PWA : remonter la barre du bas quand le clavier est ouvert (comme la zone de saisie Oliver)
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined' || !window.visualViewport) return;
    const handler = () => setKeyboardOffset(Math.max(0, window.innerHeight - window.visualViewport.height));
    window.visualViewport.addEventListener('resize', handler);
    window.visualViewport.addEventListener('scroll', handler);
    return () => {
      window.visualViewport.removeEventListener('resize', handler);
      window.visualViewport.removeEventListener('scroll', handler);
    };
  }, [isMobile]);

  useEffect(() => {
    setIsDarkTheme(document.documentElement.classList.contains('theme-dark'));
  }, []);
  useEffect(() => {
    const handleThemeUpdated = () => setIsDarkTheme(document.documentElement.classList.contains('theme-dark'));
    window.addEventListener('themeUpdated', handleThemeUpdated);
    return () => window.removeEventListener('themeUpdated', handleThemeUpdated);
  }, []);

  // Fonction pour obtenir l'icône mobile selon le texte
  const getMobileIcon = useMemo(() => {
    return (text) => {
      if (!isMobile) return null;
      
      const iconMap = {
        'Accueil': (
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
            <path d="M480-790.77 760-580v420H200v-420l280-210.77Zm70.77 381.63Q580-438.28 580-479.91q0-41.63-29.14-70.86Q521.72-580 480.09-580q-41.63 0-70.86 29.14Q380-521.72 380-480.09q0 41.63 29.14 70.86Q438.28-380 479.91-380q41.63 0 70.86-29.14Zm-113.12-28.51Q420-455.31 420-480t17.65-42.35Q455.31-540 480-540t42.35 17.65Q540-504.69 540-480t-17.65 42.35Q504.69-420 480-420t-42.35-17.65ZM477.94-260q-51.25 0-99.48 15.38-48.23 15.39-90.92 44.62h381.08q-41.93-29.23-90.68-44.62-48.75-15.38-100-15.38ZM240-560v347.69q50.46-42.07 111.21-64.88Q411.96-300 477.69-300q67.08 0 129.08 22.42 62 22.43 113.23 64.5V-560L480-740 240-560Zm240 80Z"/>
          </svg>
        ),
        'Portefeuille': (
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
            <path d="M200-280v80-560 480Zm24.62 120q-26.85 0-45.74-18.88Q160-197.77 160-224.62v-510.76q0-26.85 18.88-45.74Q197.77-800 224.62-800h510.76q26.85 0 45.74 18.88Q800-762.23 800-735.38v117.69h-40v-117.69q0-10.77-6.92-17.7-6.93-6.92-17.7-6.92H224.62q-10.77 0-17.7 6.92-6.92 6.93-6.92 17.7v510.76q0 10.77 6.92 17.7 6.93 6.92 17.7 6.92h510.76q10.77 0 17.7-6.92 6.92-6.93 6.92-17.7v-117.69h40v117.69q0 26.85-18.88 45.74Q762.23-160 735.38-160H224.62Zm320-160q-26.85 0-45.74-18.88Q480-357.77 480-384.62v-190.76q0-26.85 18.88-45.74Q517.77-640 544.62-640h230.76q26.85 0 45.74 18.88Q840-602.23 840-575.38v190.76q0 26.85-18.88 45.74Q802.23-320 775.38-320H544.62Zm230.76-40q10.77 0 17.7-6.92 6.92-6.93 6.92-17.7v-190.76q0-10.77-6.92-17.7-6.93-6.92-17.7-6.92H544.62q-10.77 0-17.7 6.92-6.92 6.93-6.92 17.7v190.76q0 10.77 6.92 17.7 6.93 6.92 17.7 6.92h230.76Zm-92.88-77.5Q700-455 700-480t-17.5-42.5Q665-540 640-540t-42.5 17.5Q580-505 580-480t17.5 42.5Q615-420 640-420t42.5-17.5Z"/>
          </svg>
        ),
        'Compte': (
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
            <path d="m296-348.46 122-121.23 80 80 162-161.77V-460h40v-160H540v40h91.46L498-446.54l-80-80L268.46-376 296-348.46ZM224.62-160q-27.62 0-46.12-18.5Q160-197 160-224.62v-510.76q0-27.62 18.5-46.12Q197-800 224.62-800h510.76q27.62 0 46.12 18.5Q800-763 800-735.38v510.76q0 27.62-18.5 46.12Q763-160 735.38-160H224.62Zm0-40h510.76q9.24 0 16.93-7.69 7.69-7.69 7.69-16.93v-510.76q0-9.24-7.69-16.93-7.69-7.69-16.93-7.69H224.62q-9.24 0-16.93 7.69-7.69 7.69-7.69 16.93v510.76q0 9.24 7.69 16.93 7.69 7.69 16.93 7.69ZM200-760v560-560Z"/>
          </svg>
        ),
        'Stock screener': (
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
            <path d="m720-459.23 40 40v234.61q0 27.62-18.5 46.12Q723-120 695.38-120H184.62q-27.62 0-46.12-18.5Q120-157 120-184.62v-510.76q0-27.62 18.5-46.12Q157-760 184.62-760h214.61q-3.38 11.08-4.69 20.81-1.31 9.73-2.54 19.19H184.62q-9.24 0-16.93 7.69-7.69 7.69-7.69 16.93v510.76q0 9.24 7.69 16.93 7.69 7.69 16.93 7.69h510.76q9.24 0 16.93-7.69 7.69-7.69 7.69-16.93v-274.61Zm18.15-150.92 132.62 132.61-28.31 28.31-132.61-132.62q-17.93 15.85-41.54 23.47-23.62 7.61-48.31 7.61-62.69 0-105.96-43.27-43.27-43.27-43.27-105.96 0-62.69 43.27-105.96 43.27-43.27 105.96-43.27 62.69 0 105.96 43.27 43.27 43.27 43.27 105.96 0 27-8.38 49.46-8.39 22.46-22.7 40.39Zm-40.61-12.31q31.69-31.69 31.69-77.54 0-45.85-31.69-77.54-31.69-31.69-77.54-31.69-45.85 0-77.54 31.69-31.69 31.69-31.69 77.54 0 45.85 31.69 77.54 31.69 31.69 77.54 31.69 45.85 0 77.54-31.69ZM160-459.23V-160v-560 249.23V-482v22.77Z"/>
          </svg>
        ),
        'Analyse': (
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f">
            <path d="M400-318.46q100 0 170-70.23 70-70.22 70-170.54t-70-170.55Q500-800 400-800t-170 70.22q-70 70.23-70 170.55 0 100.32 70 170.54 70 70.23 170 70.23Zm-20-137.69v-238.47h40v238.47h-40Zm-133.85 0v-162.31h40v162.31h-40Zm267.7 0V-580h40v123.85h-40ZM812.46-120 583.92-348.54q-37.15 32-84.19 50.27Q452.69-280 400-280q-117.25 0-198.63-81.34Q120-442.68 120-559.88q0-117.2 81.34-198.66T399.88-840q117.2 0 198.66 81.37Q680-677.25 680-560q0 52.69-18.27 99.73-18.27 47.04-50.27 83.42L840-148.31 812.46-120Z"/>
          </svg>
        ),
        'Macro': (
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
            <path d="M480-120q-74.54 0-140.23-28.42-65.69-28.43-114.31-77.04-48.61-48.62-77.04-114.31Q120-405.46 120-480q0-74.54 28.42-140.23 28.43-65.69 77.04-114.31 48.62-48.61 114.31-77.04Q405.46-840 480-840q139.85 0 241.27 92.27 101.42 92.27 116.11 227.96h-40.46q-10.54-86.85-62.73-155.88Q682-744.69 600-777.54V-760q0 33-23.5 56.5T520-680h-80v80q0 17-11.5 28.5T400-560h-80v80h73.85v120H360L168-552q-3 18-5.5 36t-2.5 36q0 131 92 225t228 95v40Zm360.92-10.77-138-136.46q-17.92 12.77-38.84 20Q643.15-240 620-240q-58.85 0-99.42-40.58Q480-321.15 480-380t40.58-99.42Q561.15-520 620-520t99.42 40.58Q760-438.85 760-380q0 23.92-7.62 45.23-7.61 21.31-21.15 39.23l138 136.46-28.31 28.31ZM691-309q29-29 29-71t-29-71q-29-29-71-29t-71 29q-29 29-29 71t29 71q29 29 71 29t71-29Z"/>
          </svg>
        ),
        'Oliver': (
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill={isDarkTheme ? '#e0e0e0' : '#1f1f1f'}>
            <path d="M489.46-373.62q55.39 0 95.54-35.76 40.15-35.77 40.15-87.47 0-43.92-27.65-74.96-27.65-31.04-68.88-31.04-34.7 0-59.89 22.7-25.19 22.69-25.19 55.15 0 14.38 5.96 27.77 5.96 13.38 16.88 25.31l30.85-27.77q-6.85-5.08-10.27-11.16-3.42-6.07-3.42-13.15 0-14.08 11.69-25.96 11.69-11.89 33.39-11.89 23.07 0 39.92 18.04 16.84 18.04 16.84 47.96 0 33.31-26.65 57.89t-68.27 24.58q-47.77 0-82.19-38-34.42-38-34.42-95.31 0-29 11.38-55.89 11.39-26.88 32.92-48.42l-29.3-28.54q-26.62 26.39-41.31 60.46-14.69 34.08-14.69 71.39 0 71.84 45.23 122.96 45.23 51.11 111.38 51.11ZM280-120v-150.46q-57-52-88.5-116.89Q160-452.23 160-520.77q0-133.08 93.46-226.15Q346.92-840 480-840q108.08 0 195.35 65.81t113.19 169.96L829.77-440q4.23 15.15-5.46 27.58Q814.62-400 798.46-400H720v135.38q0 26.85-18.88 45.74Q682.23-200 655.38-200H560v80h-40v-120h135.38q10.77 0 17.7-6.92 6.92-6.93 6.92-17.7V-440h108l-38-155q-23-91.77-98.77-148.38Q575.46-800 480-800q-116 0-198 81.38-82 81.39-82 197.39 0 60 24.5 113.61Q249-354 294-312l26 24v168h-40Zm214-340Z"/>
          </svg>
        )
      };
      
      return iconMap[text] || (text === oliverName ? iconMap['Oliver'] : null);
    };
  }, [isMobile, isDarkTheme, oliverName]);

  // Fonction récursive pour trouver une icône SVG dans les children
  const findSvgIcon = useCallback((children) => {
    if (!children) return null;
    
    const childrenArray = React.Children.toArray(children);
    for (const child of childrenArray) {
      if (child && child.type === 'svg') {
        return child;
      }
      if (child && child.props && child.props.children) {
        const found = findSvgIcon(child.props.children);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Extraire les icônes et actions des éléments de menu depuis les children (fallback mobile si compactMenuItems vide)
  const menuItems = useMemo(() => {
    if (!children) return [];
    
    const items = [];
    React.Children.forEach(children, (child) => {
      if (child && child.props && child.props.children) {
        const nav = child.props.children;
        if (nav && nav.props && nav.props.children) {
          const ul = nav.props.children;
          const rawLis = ul && ul.props ? ul.props.children : null;
          const lis = rawLis == null ? [] : Array.isArray(rawLis) ? rawLis : [rawLis];
          lis.forEach((li) => {
            if (li && li.props && li.props.children) {
              const link = li.props.children;
              if (link && link.props) {
                const icon = findSvgIcon(link.props.children);
                const text = React.Children.toArray(link.props.children).find(
                  (c) => typeof c === 'string'
                );
                if (icon) {
                  const mobileIcon = getMobileIcon ? getMobileIcon(text) : null;
                  items.push({
                    icon: mobileIcon || icon,
                    onClick: link.props.onClick,
                    href: link.props.href,
                    active: link.props.className?.includes('active'),
                    text: text || ''
                  });
                }
              }
            }
          });
        }
      }
    });
    
    return items;
  }, [children, isMobile, getMobileIcon, findSvgIcon]);

  // Menu items pour la barre du bas mobile : tous les items du menu + Profil en fin (fallback si compactMenuItems vide)
  const mobileMenuItems = useMemo(() => {
    if (!isMobile) return [];
    const order = ['Accueil', 'Portefeuille', 'Compte', oliverName, 'Analyse', 'Macro', 'Paramètres'];
    const ordered = order
      .map((text) => menuItems.find((item) => item.text === text || (text === oliverName && item.text === 'Oliver')))
      .filter(Boolean);
    if (profile && onAvatarClick) {
      ordered.push({
        icon: null,
        onClick: onAvatarClick,
        href: null,
        active: activePage === 'parametres',
        text: 'Profil',
        isAvatar: true
      });
    }
    return ordered.length > 0 ? ordered : menuItems.concat(profile && onAvatarClick ? [{
      icon: null,
      onClick: onAvatarClick,
      href: null,
      active: activePage === 'parametres',
      text: 'Profil',
      isAvatar: true
    }] : []);
  }, [menuItems, isMobile, profile, onAvatarClick, activePage, oliverName]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
      // Fermer le menu mobile si on passe en desktop
      if (!mobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Détecter les changements d'état de la sidebar pour déclencher l'animation uniquement lors de la transition
  useEffect(() => {
    // Au premier montage, initialiser la référence et ne pas déclencher l'animation
    if (isInitialMount) {
      prevIsOpenRef.current = isOpen;
      setIsInitialMount(false);
      return;
    }

    // Détecter un changement d'état (fermeture de la sidebar uniquement pour l'avatar compact)
    if (!isOpen && prevIsOpenRef.current) {
      // La sidebar vient de se fermer (elle était ouverte avant)
      setIsTransitioning(true);
      
      // Réinitialiser l'état de transition après l'animation
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 500); // Durée de l'animation + un peu de marge

      prevIsOpenRef.current = isOpen;
      return () => clearTimeout(timer);
    } else {
      // Mettre à jour la référence même si pas d'animation
      prevIsOpenRef.current = isOpen;
      setIsTransitioning(false);
    }
  }, [isOpen, isInitialMount]);

  const getToggleIcon = () => {
    // Icône hamburger pour mobile
    const hamburgerIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" className="sidebar-toggle-icon">
        <path d="M120-240v-80h720v80H120Zm0-200v-80h720v80H120Zm0-200v-80h720v80H120Z"/>
      </svg>
    );
    
    // Icône pour fermer (quand ouvert) — desktop
    const closeIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f" className="sidebar-toggle-icon">
        <path d="m256-236-20-20 224-224-224-224 20-20 224 224 224-224 20 20-224 224 224 224-20 20-224-224-224 224Z"/>
      </svg>
    );
    
    // Icône pour ouvrir (quand fermé) — desktop
    const openIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f" className="sidebar-toggle-icon">
        <path d="M172-292v-28h438v28H172Zm596-35L613-480l155-152 20 20-135 132 135 133-20 20ZM172-466v-28h326v28H172Zm0-174v-28h438v28H172Z"/>
      </svg>
    );

    // Sur mobile, toujours afficher l'icône hamburger
    if (isMobile) {
      return mobileMenuOpen ? closeIcon : hamburgerIcon;
    }

    // Sur desktop, utiliser les icônes originales
    if (position === 'right') {
      return isOpen ? closeIcon : openIcon;
    }
    // Pour position 'left' : si ouvert → icône de fermeture, si fermé → icône d'ouverture
    return isOpen ? closeIcon : openIcon;
  };

  const handleToggle = () => {
    if (isMobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      onToggle();
    }
  };

  const handleOverlayClick = () => {
    if (isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Bouton hamburger fixe sur mobile - CACHÉ */}
      {/* {isMobile && (
        <button 
          className="sidebar-hamburger-button"
          onClick={handleToggle}
          aria-label="Toggle menu"
        >
          {getToggleIcon()}
        </button>
      )} */}

      {/* Overlay pour mobile - CACHÉ */}
      {/* {isMobile && mobileMenuOpen && (
        <div className="sidebar-overlay" onClick={handleOverlayClick} />
      )} */}

      <div
        className={`sidebar sidebar-${position} ${isMobile ? 'sidebar-closed sidebar-mobile' : (isOpen ? 'sidebar-open' : 'sidebar-closed')} ${isDarkTheme ? 'theme-dark' : ''}`}
        style={isMobile && keyboardOffset > 0 ? { bottom: keyboardOffset } : undefined}
      >
        {!isMobile && (
          <div className="sidebar-toggle-container">
            <button className="sidebar-toggle" onClick={onToggle}>
              {getToggleIcon()}
            </button>
          </div>
        )}
        <div 
          className="sidebar-logo"
          onClick={isMobile && onLogoClick ? (e) => {
            e.preventDefault();
            onLogoClick();
          } : undefined}
          style={isMobile && onLogoClick ? { cursor: 'pointer' } : {}}
        >
          <img src={logoUrl || logoOliverSmall} alt="Logo" />
        </div>
        <div className="sidebar-header">
          <h2>Navigation</h2>
        </div>
        <div className="sidebar-content">
          {children}
        </div>
        {/* Menu compact : sur desktop quand sidebar fermée, sur mobile toujours */}
        {(!isMobile && (
          <div className={`sidebar-compact-menu ${isOpen ? 'sidebar-compact-menu-hidden' : ''}`}>
            {(compactMenuItems && compactMenuItems.length > 0 ? compactMenuItems : menuItems).map((item, index) => {
              const label = item.label ?? item.text ?? '';
              const isParametres = item.isParametres ?? (item.text === 'Paramètres');
              const iconSvg = item.iconSvg;
              const button = (
                <button
                  key={item.id ?? index}
                  className={`sidebar-compact-item ${item.active ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.onClick) item.onClick(e);
                  }}
                  aria-label={label}
                  style={{ position: 'relative' }}
                >
                  {iconSvg ? (
                    <span className="sidebar-compact-icon-wrapper" style={{ position: 'relative', display: 'inline-block', width: '24px', height: '24px' }}>
                      <span
                        className="sidebar-compact-icon"
                        dangerouslySetInnerHTML={{ __html: (typeof iconSvg === 'string' ? iconSvg : '').replace(/20px/g, '24px').replace(/\bfill="[^"]*"/g, 'fill="currentColor"').replace(/\bstroke="[^"]*"/g, 'stroke="currentColor"') }}
                        style={{ display: 'block', width: '24px', height: '24px' }}
                      />
                      {isParametres && (
                        <span
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: isConnected ? '#34C759' : '#FF3B30',
                            border: '1px solid white',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            pointerEvents: 'none'
                          }}
                          title={isConnected ? 'Connecté à IB Gateway' : 'Déconnecté de IB Gateway'}
                        />
                      )}
                    </span>
                  ) : item.icon ? (
                    isParametres ? (
                      <span style={{ position: 'relative', display: 'inline-block', width: '24px', height: '24px' }}>
                        {React.cloneElement(item.icon, { className: 'sidebar-compact-icon', width: '24px', height: '24px', fill: 'currentColor', stroke: 'currentColor', style: { display: 'block' } })}
                        <span style={{ position: 'absolute', bottom: 0, right: 0, width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isConnected ? '#34C759' : '#FF3B30', border: '1px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)', zIndex: 10, pointerEvents: 'none' }} title={isConnected ? 'Connecté à IB Gateway' : 'Déconnecté de IB Gateway'} />
                      </span>
                    ) : (
                      React.cloneElement(item.icon, { className: 'sidebar-compact-icon', width: '24px', height: '24px', fill: 'currentColor', stroke: 'currentColor' })
                    )
                  ) : null}
                </button>
              );
              return (
                <Tooltip key={item.id ?? index} title={label} placement="right" arrow enterDelay={300} leaveDelay={0} slotProps={menuTooltipSlotProps}>
                  {button}
                </Tooltip>
              );
            })}
            {profile && (
              <div key={`compact-avatar-${profile.user_id || profile.id || profile.email}`} className={`sidebar-compact-user ${isTransitioning && !isOpen ? 'sidebar-avatar-animate' : ''}`}>
                <Tooltip
                  title={`${profile.name || 'Utilisateur'}\n${profile.email || ''}`}
                  placement="right"
                  arrow
                  enterDelay={300}
                  leaveDelay={0}
                  slotProps={menuTooltipSlotProps}
                >
                  <div 
                    className={`sidebar-compact-user-avatar ${profile.picture ? 'sidebar-compact-user-avatar-with-photo' : ''}`}
                    onClick={(e) => {
                      if (onAvatarClick) {
                        e.preventDefault();
                        onAvatarClick();
                      }
                    }}
                    style={{ cursor: onAvatarClick ? 'pointer' : 'default' }}
                  >
                  {profile.picture ? (
                    <img 
                      src={profile.picture.startsWith('data:') ? profile.picture : `${profile.picture}?t=${Date.now()}`}
                      alt={profile.name || 'User'}
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const initialsDiv = e.target.nextSibling;
                        if (initialsDiv) {
                          initialsDiv.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div 
                    className="sidebar-compact-user-initials"
                    style={{ display: profile.picture ? 'none' : 'flex' }}
                  >
                    {(() => {
                      const name = profile.name || 'User';
                      return name.split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                    })()}
                  </div>
                </div>
                </Tooltip>
              </div>
            )}
          </div>
        )) || (isMobile && (
          <div className="sidebar-compact-menu">
            {(compactMenuItems && compactMenuItems.length > 0 ? compactMenuItems : mobileMenuItems).map((item, index) => {
              const displayName = item.label ?? item.text ?? '';
              const isParametres = item.isParametres ?? (item.text === 'Paramètres');
              const iconSvg = item.iconSvg;
              const handleClick = (e) => {
                e.preventDefault();
                const button = e.currentTarget;
                button.classList.add('sidebar-compact-item-clicked');
                setTimeout(() => button.classList.remove('sidebar-compact-item-clicked'), 500);
                if (item.onClick) item.onClick(e);
              };
              return (
                <button
                  key={item.id ?? index}
                  className={`sidebar-compact-item sidebar-compact-item-mobile ${item.active ? 'active' : ''}`}
                  onClick={handleClick}
                  title={item.isAvatar ? `${profile?.name || 'Utilisateur'}\n${profile?.email || ''}` : displayName}
                  aria-label={item.isAvatar ? 'Profil' : displayName}
                >
                  <div className="sidebar-compact-item-content">
                    {item.isAvatar && profile ? (
                      <span style={{ position: 'relative', display: 'inline-block', width: '24px', height: '24px' }}>
                        {profile.picture ? (
                          <img
                            src={profile.picture.startsWith('data:') ? profile.picture : `${profile.picture}?t=${Date.now()}`}
                            alt={profile.name || 'User'}
                            referrerPolicy="no-referrer"
                            style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover', display: 'block', border: '1px solid #eee' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const init = e.target.nextElementSibling;
                              if (init) init.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className="sidebar-compact-user-initials"
                          style={{
                            display: profile.picture ? 'none' : 'flex',
                            width: '24px', height: '24px', borderRadius: '50%',
                            fontSize: '10px', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: item.active ? 'var(--accent-color)' : 'rgb(142, 142, 147)',
                            color: 'white', position: 'absolute', top: 0, left: 0
                          }}
                        >
                          {(profile.name || 'User').split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <span
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: isConnected ? '#34C759' : '#FF3B30',
                            border: '1px solid white',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            pointerEvents: 'none'
                          }}
                          title={isConnected ? 'Connecté à IB Gateway' : 'Déconnecté de IB Gateway'}
                        />
                      </span>
                    ) : iconSvg ? (
                      <span style={{ position: 'relative', display: 'inline-block' }} className="sidebar-compact-icon-mobile-wrapper">
                        <span
                          className="sidebar-compact-icon"
                          dangerouslySetInnerHTML={{ __html: (typeof iconSvg === 'string' ? iconSvg : '').replace(/20px/g, '24px').replace(/\bfill="[^"]*"/g, 'fill="currentColor"').replace(/\bstroke="[^"]*"/g, 'stroke="currentColor"') }}
                          style={{ display: 'block' }}
                        />
                        {isParametres && (
                          <span
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: isConnected ? '#34C759' : '#FF3B30',
                              border: '1px solid white',
                              boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                              zIndex: 10,
                              pointerEvents: 'none'
                            }}
                            title={isConnected ? 'Connecté à IB Gateway' : 'Déconnecté de IB Gateway'}
                          />
                        )}
                      </span>
                    ) : displayName === 'Paramètres' && item.icon ? (
                      <span style={{ position: 'relative', display: 'inline-block' }} className="sidebar-compact-icon-mobile-wrapper">
                        {React.cloneElement(item.icon, { className: 'sidebar-compact-icon', style: { display: 'block' } })}
                        <span
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: isConnected ? '#34C759' : '#FF3B30',
                            border: '1px solid white',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            pointerEvents: 'none'
                          }}
                          title={isConnected ? 'Connecté à IB Gateway' : 'Déconnecté de IB Gateway'}
                        />
                      </span>
                    ) : (
                      item.icon && (
                        <span style={{ position: 'relative', display: 'inline-block' }} className="sidebar-compact-icon-mobile-wrapper">
                          {React.cloneElement(item.icon, { className: 'sidebar-compact-icon', style: { display: 'block' } })}
                        </span>
                      )
                    )}
                    <span className="sidebar-compact-item-label">{displayName}</span>
                  </div>
                </button>
              );
            })}
            {compactMenuItems && compactMenuItems.length > 0 && profile && onAvatarClick && (
              <button
                key="mobile-profil"
                className={`sidebar-compact-item sidebar-compact-item-mobile ${activePage === 'parametres' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  const button = e.currentTarget;
                  button.classList.add('sidebar-compact-item-clicked');
                  setTimeout(() => button.classList.remove('sidebar-compact-item-clicked'), 500);
                  onAvatarClick(e);
                }}
                title={`${profile.name || 'Utilisateur'}\n${profile.email || ''}`}
                aria-label="Profil"
              >
                <div className="sidebar-compact-item-content">
                  <span style={{ position: 'relative', display: 'inline-block', width: '24px', height: '24px' }}>
                    {profile.picture ? (
                      <img
                        src={profile.picture.startsWith('data:') ? profile.picture : `${profile.picture}?t=${Date.now()}`}
                        alt={profile.name || 'User'}
                        referrerPolicy="no-referrer"
                        style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover', display: 'block', border: '1px solid #eee' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const init = e.target.nextElementSibling;
                          if (init) init.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="sidebar-compact-user-initials"
                      style={{
                        display: profile.picture ? 'none' : 'flex',
                        width: '24px', height: '24px', borderRadius: '50%',
                        fontSize: '10px', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: activePage === 'parametres' ? 'var(--accent-color)' : 'rgb(142, 142, 147)',
                        color: 'white', position: 'absolute', top: 0, left: 0
                      }}
                    >
                      {(profile.name || 'User').split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <span
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: isConnected ? '#34C759' : '#FF3B30',
                        border: '1px solid white',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                        zIndex: 10,
                        pointerEvents: 'none'
                      }}
                      title={isConnected ? 'Connecté à IB Gateway' : 'Déconnecté de IB Gateway'}
                    />
                  </span>
                  <span className="sidebar-compact-item-label">Profil</span>
                </div>
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default Sidebar;

